// The backfill CTA (profile spec §3.2, §5.4, §8 B6).
//
// A paired member with no birthday never gets a generated birthday row: the
// only creator ran from `pair.confirmed`, whose skip branch carried a comment
// promising a UI CTA that was never built — the defect B6 names. Filling the
// birthday in fires `user.profile_updated`, whose listener runs the same
// creation, so the anniversary appears without anything being replayed.
//
// Placed on **both** the home page and the profile page, per spec §3.2: the
// home page is where a person lands, and a CTA they have to go looking for is
// not a CTA.

import { Cake } from "lucide-react";
import { InfoBanner } from "@/components/primitives/InfoBanner";
import { cn } from "@/lib/utils/cn";

export interface BirthdayBackfillCtaProps {
  /** `card` for the profile page, `inline` above the picker. */
  variant?: "card" | "inline";
  onClick?: () => void;
  className?: string;
}

export function BirthdayBackfillCta({
  variant = "card",
  onClick,
  className,
}: BirthdayBackfillCtaProps) {
  const body = (
    <>
      {/* No icon here. The `card` variant passes one to `InfoBanner`'s icon
          slot, and the `inline` variant is rendered without a banner chrome at
          all — drawing a cake in the body meant the card variant showed the
          same cake twice, side by side. */}
      <span className="block text-body-sm font-semibold text-ink-1">還沒有生日</span>
      <span className="mt-s-1 block text-body-sm text-ink-2">
        填上生日,你們的紀念日裡就會多一個每年都會回到的日子
      </span>
      {onClick ? (
        <span
          className="mt-s-3 inline-flex items-center rounded-pill bg-ink-1 px-s-4 py-s-2 text-body-sm font-semibold text-paper"
          data-testid="backfill-cta-action"
        >
          現在填
        </span>
      ) : null}
    </>
  );

  if (variant === "inline") {
    return (
      <div
        data-testid="birthday-backfill-cta"
        className={cn("rounded-lg border border-hairline bg-paper-tint-1 p-s-4", className)}
      >
        {body}
      </div>
    );
  }

  if (!onClick) {
    // No handler → render the copy, not a button. A disabled control that
    // looks exactly like a CTA teaches people that CTAs here do nothing.
    return (
      <InfoBanner tone="paper-deep" icon={<Cake size={18} strokeWidth={1.5} aria-hidden="true" />} className={className}>
        <div data-testid="birthday-backfill-cta">{body}</div>
      </InfoBanner>
    );
  }

  return (
    <InfoBanner tone="paper-deep" icon={<Cake size={18} strokeWidth={1.5} aria-hidden="true" />} className={className}>
      <div data-testid="birthday-backfill-cta">
        <button
          type="button"
          onClick={onClick}
          className="tap-target min-h-11 w-full text-left"
          data-testid="backfill-cta"
        >
          {body}
        </button>
      </div>
    </InfoBanner>
  );
}
